import * as PIXI from 'pixi.js';
import { Vector2 } from '../utils/Vector2';

interface DamageNumber {
  text: PIXI.Text;
  startTime: number;
  position: Vector2;
  velocity: Vector2;
  lifetime: number; // in seconds
}

/**
 * Manages floating damage numbers for critical hits
 * Limits to MAX_DAMAGE_NUMBERS to prevent performance issues
 */
export class DamageNumbers {
  private container: PIXI.Container;
  private damageNumbers: DamageNumber[] = [];
  private readonly MAX_DAMAGE_NUMBERS = 6; // Limit for late game performance
  private readonly LIFETIME = 1.5; // seconds
  private readonly FLOAT_SPEED = 30; // pixels per second (upward)
  
  constructor() {
    this.container = new PIXI.Container();
  }
  
  /**
   * Spawn a "MISS" indicator for evaded attacks
   */
  spawnMissNumber(position: Vector2): void {
    // Remove oldest if at limit
    if (this.damageNumbers.length >= this.MAX_DAMAGE_NUMBERS) {
      const oldest = this.damageNumbers.shift();
      if (oldest) {
        this.container.removeChild(oldest.text);
        oldest.text.destroy();
      }
    }
    
    // Create miss text
    const text = new PIXI.Text('MISS', {
      fontFamily: 'Arial',
      fontSize: 18,
      fill: 0xFFFF00, // Yellow for misses
      fontWeight: 'bold',
      stroke: 0x000000,
      strokeThickness: 3,
    });
    
    // Center the text on position
    text.anchor.set(0.5, 0.5);
    text.x = position.x;
    text.y = position.y - 15; // Start slightly above enemy
    
    // Add slight random offset to prevent perfect stacking
    text.x += (Math.random() - 0.5) * 10;
    
    this.container.addChild(text);
    
    // Track damage number
    this.damageNumbers.push({
      text,
      startTime: Date.now(),
      position: position.clone(),
      velocity: new Vector2(0, -this.FLOAT_SPEED), // Float upward
      lifetime: this.LIFETIME,
    });
  }
  
  /**
   * Spawn a critical hit damage number
   */
  spawnCritNumber(damage: number, position: Vector2): void {
    // Remove oldest if at limit
    if (this.damageNumbers.length >= this.MAX_DAMAGE_NUMBERS) {
      const oldest = this.damageNumbers.shift();
      if (oldest) {
        this.container.removeChild(oldest.text);
        oldest.text.destroy();
      }
    }
    
    // Create damage text
    const text = new PIXI.Text(`${Math.floor(damage)}`, {
      fontFamily: 'Arial',
      fontSize: 21,
      fill: 0xff3333, // Bright red for crits
      fontWeight: 'bold',
      stroke: 0x000000,
      strokeThickness: 3,
    });
    
    // Center the text on position
    text.anchor.set(0.5, 0.5);
    text.x = position.x;
    text.y = position.y - 15; // Start slightly above enemy
    
    // Add slight random offset to prevent perfect stacking
    text.x += (Math.random() - 0.5) * 10;
    
    this.container.addChild(text);
    
    // Track damage number
    this.damageNumbers.push({
      text,
      startTime: Date.now(),
      position: position.clone(),
      velocity: new Vector2(0, -this.FLOAT_SPEED), // Float upward
      lifetime: this.LIFETIME,
    });
  }
  
  /**
   * Spawn an "EXECUTED + damage" indicator for sniper instant kills
   */
  spawnExecutedNumber(damage: number, position: Vector2): void {
    // Remove oldest if at limit
    if (this.damageNumbers.length >= this.MAX_DAMAGE_NUMBERS) {
      const oldest = this.damageNumbers.shift();
      if (oldest) {
        this.container.removeChild(oldest.text);
        oldest.text.destroy();
      }
    }
    
    // Create executed text
    const text = new PIXI.Text(`EXECUTED\n${Math.floor(damage)}`, {
      fontFamily: 'Arial',
      fontSize: 18,
      fill: 0xFF00FF, // Magenta for executions
      fontWeight: 'bold',
      stroke: 0x000000,
      strokeThickness: 3,
      align: 'center',
    });
    
    // Center the text on position
    text.anchor.set(0.5, 0.5);
    text.x = position.x;
    text.y = position.y - 15; // Start slightly above enemy
    
    // Add slight random offset to prevent perfect stacking
    text.x += (Math.random() - 0.5) * 10;
    
    this.container.addChild(text);
    
    // Track damage number
    this.damageNumbers.push({
      text,
      startTime: Date.now(),
      position: position.clone(),
      velocity: new Vector2(0, -this.FLOAT_SPEED), // Float upward
      lifetime: this.LIFETIME,
    });
  }
  
  /**
   * Spawn an armor reduction indicator "- X% armor"
   */
  spawnArmorReductionNumber(armorPercent: number, position: Vector2): void {
    // Remove oldest if at limit
    if (this.damageNumbers.length >= this.MAX_DAMAGE_NUMBERS) {
      const oldest = this.damageNumbers.shift();
      if (oldest) {
        this.container.removeChild(oldest.text);
        oldest.text.destroy();
      }
    }
    
    // Create armor reduction text
    const text = new PIXI.Text(`-${Math.floor(armorPercent * 100)}% armor`, {
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0xFFA500, // Orange for armor reduction
      fontWeight: 'bold',
      stroke: 0x000000,
      strokeThickness: 2,
    });
    
    // Center the text on position
    text.anchor.set(0.5, 0.5);
    text.x = position.x;
    text.y = position.y - 15; // Start slightly above enemy
    
    // Add slight random offset to prevent perfect stacking
    text.x += (Math.random() - 0.5) * 10;
    
    this.container.addChild(text);
    
    // Track damage number
    this.damageNumbers.push({
      text,
      startTime: Date.now(),
      position: position.clone(),
      velocity: new Vector2(0, -this.FLOAT_SPEED), // Float upward
      lifetime: this.LIFETIME,
    });
  }
  
  /**
   * Update all damage numbers (call every frame)
   */
  update(deltaTime: number): void {
    const now = Date.now();
    
    // Update all active damage numbers
    for (let i = this.damageNumbers.length - 1; i >= 0; i--) {
      const dmgNum = this.damageNumbers[i];
      const elapsed = (now - dmgNum.startTime) / 1000; // Convert to seconds
      
      // Remove if expired
      if (elapsed >= dmgNum.lifetime) {
        this.container.removeChild(dmgNum.text);
        dmgNum.text.destroy();
        this.damageNumbers.splice(i, 1);
        continue;
      }
      
      // Update position (float upward)
      dmgNum.text.y += dmgNum.velocity.y * deltaTime;
      
      // Fade out based on lifetime (fade starts at 50% lifetime)
      const fadeStart = dmgNum.lifetime * 0.5;
      if (elapsed > fadeStart) {
        const fadeProgress = (elapsed - fadeStart) / (dmgNum.lifetime - fadeStart);
        dmgNum.text.alpha = 1.0 - fadeProgress;
      }
      
      // Scale up slightly at start for "pop" effect
      if (elapsed < 0.1) {
        const scaleProgress = elapsed / 0.1;
        dmgNum.text.scale.set(0.8 + scaleProgress * 0.4); // Scale from 0.8 to 1.2
      } else if (dmgNum.text.scale.x > 1.0) {
        // Scale back to 1.0 after pop
        dmgNum.text.scale.set(Math.max(1.0, dmgNum.text.scale.x - deltaTime * 2));
      }
    }
  }
  
  /**
   * Get the container to add to the scene
   */
  getContainer(): PIXI.Container {
    return this.container;
  }
  
  /**
   * Clear all damage numbers
   */
  clear(): void {
    for (const dmgNum of this.damageNumbers) {
      this.container.removeChild(dmgNum.text);
      dmgNum.text.destroy();
    }
    this.damageNumbers = [];
  }
  
  /**
   * Get current count of active damage numbers
   */
  getActiveCount(): number {
    return this.damageNumbers.length;
  }
}

